// public/js/features/pois/poi-type-modal.js
// Create/edit a POI type (icon + colour). Scope is fixed by the caller
// (a tenant id, or 'public' for platform admins).
// Exposes window.openPoiTypeModal({ mode, type?, scope, onSaved }).
import { createPoiType, updatePoiType, deletePoiType } from './api.js';
import { refreshPoiData } from './poi-data.js';
import { poiState } from './poi-state.js';
import { can, getCurrentUser } from '../../modules/rbac.js';

const ICON_SUGGESTIONS = [
  'fa-solid fa-location-dot', 'fa-solid fa-warehouse', 'fa-solid fa-plane',
  'fa-solid fa-gas-pump', 'fa-solid fa-wrench', 'fa-solid fa-square-parking',
  'fa-solid fa-building', 'fa-solid fa-truck', 'fa-solid fa-triangle-exclamation',
  'fa-solid fa-flag', 'fa-solid fa-house', 'fa-solid fa-anchor',
];

let modalInstance = null;
let ctx = { mode: 'create', typeId: null, scope: 'public', onSaved: null };

export function createPoiTypeModal() {
  if (document.getElementById('poiTypeModal')) return;
  const html = `
    <div class="modal fade" id="poiTypeModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="poi-type-modal-title">New POI Type</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-2"><span id="poi-type-scope-badge" class="badge bg-info"></span></div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Name <span class="text-danger">*</span></label>
              <input id="poi-type-name" class="form-control" type="text" maxlength="80">
            </div>
            <div class="mb-3">
              <label class="form-label fw-semibold">Short name</label>
              <input id="poi-type-shortname" class="form-control" type="text" maxlength="40">
            </div>
            <div class="row g-2 mb-3 align-items-end">
              <div class="col">
                <label class="form-label fw-semibold">Icon (Font Awesome class)</label>
                <input id="poi-type-icon" class="form-control" type="text" placeholder="fa-solid fa-warehouse">
              </div>
              <div class="col-auto">
                <label class="form-label fw-semibold">Colour</label><br>
                <input id="poi-type-color" class="form-control form-control-color" type="color" value="#0d6efd">
              </div>
              <div class="col-auto">
                <label class="form-label fw-semibold d-block">Preview</label>
                <span id="poi-type-preview" style="display:inline-flex;width:34px;height:34px;border-radius:8px;align-items:center;justify-content:center;color:#fff;font-size:16px;background:#0d6efd;">
                  <i class="fa-solid fa-location-dot"></i>
                </span>
              </div>
            </div>
            <div class="mb-1 small text-muted">Quick pick:</div>
            <div id="poi-type-icon-suggestions" class="d-flex flex-wrap gap-2 mb-2"></div>
          </div>
          <div class="modal-footer justify-content-between">
            <button type="button" id="poi-type-delete-btn" class="btn btn-outline-danger d-none">
              <i class="fa-solid fa-trash me-1"></i>Delete
            </button>
            <div class="ms-auto">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button type="button" id="poi-type-save-btn" class="btn btn-success"><i class="fa-solid fa-save me-1"></i>Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>`;
  document.body.insertAdjacentHTML('beforeend', html);

  const modalEl = document.getElementById('poiTypeModal');
  modalInstance = new bootstrap.Modal(modalEl);

  const iconInput = document.getElementById('poi-type-icon');
  const colorInput = document.getElementById('poi-type-color');
  iconInput.addEventListener('input', updatePreview);
  colorInput.addEventListener('input', updatePreview);

  const sugg = document.getElementById('poi-type-icon-suggestions');
  sugg.innerHTML = ICON_SUGGESTIONS.map(ic =>
    `<button type="button" class="btn btn-sm btn-outline-secondary poi-icon-sugg" data-icon="${ic}" title="${ic}"><i class="${ic}"></i></button>`
  ).join('');
  sugg.querySelectorAll('.poi-icon-sugg').forEach(b => {
    b.addEventListener('click', () => { iconInput.value = b.dataset.icon; updatePreview(); });
  });

  document.getElementById('poi-type-save-btn').addEventListener('click', onSave);
  document.getElementById('poi-type-delete-btn').addEventListener('click', onDelete);
  console.log('✅ POI type modal created');
}

function updatePreview() {
  const icon = document.getElementById('poi-type-icon').value.trim() || 'fa-solid fa-location-dot';
  const color = document.getElementById('poi-type-color').value || '#0d6efd';
  const prev = document.getElementById('poi-type-preview');
  prev.style.background = color;
  prev.innerHTML = `<i class="${icon}"></i>`;
}

window.openPoiTypeModal = function ({ mode = 'create', type = null, scope = 'public', onSaved = null } = {}) {
  if (!document.getElementById('poiTypeModal')) createPoiTypeModal();
  ctx = { mode, typeId: type?.poiId || null, scope: type?.scope || scope, onSaved };

  document.getElementById('poi-type-modal-title').textContent = mode === 'edit' ? 'Edit POI Type' : 'New POI Type';
  document.getElementById('poi-type-scope-badge').textContent = ctx.scope === 'public' ? 'Public (all tenants)' : 'Tenant type';
  document.getElementById('poi-type-name').value = type?.name || '';
  document.getElementById('poi-type-shortname').value = type?.shortName || '';
  document.getElementById('poi-type-icon').value = type?.icon || 'fa-solid fa-location-dot';
  document.getElementById('poi-type-color').value = type?.color || '#0d6efd';
  updatePreview();

  const groups = getCurrentUser().groups || [];
  const canDelete = mode === 'edit' && (ctx.scope === 'public' ? can(groups, 'managePublicPois') : can(groups, 'managePoiTypes'));
  document.getElementById('poi-type-delete-btn').classList.toggle('d-none', !canDelete);

  modalInstance.show();
};

async function onSave() {
  const btn = document.getElementById('poi-type-save-btn');
  const original = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>Saving...`;
  try {
    const name = document.getElementById('poi-type-name').value.trim();
    if (!name) throw new Error('Name is required');
    const payload = {
      name,
      shortName: document.getElementById('poi-type-shortname').value.trim(),
      icon: document.getElementById('poi-type-icon').value.trim() || 'fa-solid fa-location-dot',
      color: document.getElementById('poi-type-color').value || '#0d6efd',
    };

    let createdId = null;
    if (ctx.mode === 'edit') {
      await updatePoiType(ctx.typeId, payload);
      window.showToast('Type updated', 'success');
    } else {
      payload.scope = ctx.scope;
      const res = await createPoiType(payload);
      createdId = res?.type?.poiId || null;
      window.showToast('Type created', 'success');
    }
    modalInstance.hide();
    // Direct call, not window.poiRefreshData?.() - an optional chain on a hook
    // that some page did not install fails silently, and a stale type list is
    // exactly the bug this refresh is here to prevent.
    await refreshPoiData();
    // A type somebody just created is one they mean to use: switch it on for
    // them, or their first POI of that type lands on a map that hides it.
    // (Types created by OTHERS still default to off - see poi-state setData.)
    if (createdId) poiState.toggleType(createdId, true);
    ctx.onSaved?.();
  } catch (err) {
    console.error(err);
    window.showToast(err.message || 'Failed to save type', 'danger');
  } finally {
    btn.disabled = false;
    btn.innerHTML = original;
  }
}

async function onDelete() {
  if (!ctx.typeId) return;
  if (!confirm('Delete this type? All its POIs in the same scope will also be removed.')) return;
  const btn = document.getElementById('poi-type-delete-btn');
  btn.disabled = true;
  try {
    await deletePoiType(ctx.typeId);
    window.showToast('Type deleted', 'success');
    modalInstance.hide();
    await refreshPoiData();
    ctx.onSaved?.();
  } catch (err) {
    window.showToast(err.message || 'Failed to delete type', 'danger');
  } finally {
    btn.disabled = false;
  }
}
